import "./Foodstuffs.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";

import DefaultFoodstuffIcon from "../../assets/imgs/default-foodstuff-icon.svg";
import FilterIcon from "../../assets/imgs/filter-icon.svg";
import CheckedIcon from "../../assets/imgs/checked-icon.svg";
import UncheckedXIcon from "../../assets/imgs/unchecked-x-icon.svg";

import foodstuffService from "../../services/foodstuff-service";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import TextInput from "../../components/TextInput/TextInput";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import { GlobalContext } from "../GlobalContext";
import { useLocation } from "react-router-dom";
import Options from "../../components/Options/Options";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import { Dropdown } from "react-bootstrap";
import NewFoodstuffModal from "../../components/NewFoodstuffModal/NewFoodstuffModal";
import { useIsMobile } from "../../hooks/useMediaQuery";

const FOOD_GROUP_LABELS = {
  FRUITS: "Frutas",
  VEGETABLES: "Vegetais",
  MEATS: "Carnes",
  DAIRY: "Laticínios",
  GRAINS: "Grãos",
  LEGUMES: "Legumes",
  OILS: "Óleos",
  SWEETS: "Doces",
  BEVERAGES: "Bebidas",
  WATER: "Água",
  OTHERS: "Outros",
};

const NAME_COLLAPSE_MAX_LENGTH = 50;

const truncateName = (name, maxLength) => {
  if (!name || name.length <= maxLength) {
    return name;
  }

  const slice = name.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(" ");

  if (lastSpace > 0) {
    return `${slice.slice(0, lastSpace)}...`;
  }

  return `${slice}...`;
};

const nextTriStateFilter = (current) => {
  if (current === undefined || current === null) {
    return true;
  }
  if (current === true) {
    return false;
  }
  return null;
};

const Foodstuffs = () => {
  const isMobile = useIsMobile();
  const [foodstuffsResponse, setFoodstuffsResponse] = useState({});
  const [foodstuffs, setFoodstuffs] = useState([]);
  const [currentFoodstuffsPage, setCurrentFoodstuffsPage] = useState();
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [foodstuffOnDelete, setFoodstuffOnDelete] = useState();
  const [deleteFoodstuffError, setDeleteFoodstuffError] = useState();
  const [searchedFoodstuffName, setSearchedFoodstuffName] = useState("");
  const [searchedFoodstuffBrand, setSearchedFoodstuffBrand] = useState("");
  const [withBarcode, setWithBarcode] = useState();
  const [withBrand, setWithBrand] = useState();
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [expandedNameRowId, setExpandedNameRowId] = useState(null);
  const searchParams = new URLSearchParams(useLocation().search);
  const foodstuffPageParam = searchParams?.get("currentPage");
  const withBarcodeParam = searchParams?.get("withBarcode");
  const withBrandParam = searchParams?.get("withBrand");
  const unverifiedParam = searchParams?.get("unverified");
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const [openOptionsDropdown, setOpenOptionsDropdown] = useState(false);
  const [showNewFoodstuffModal, setShowNewFoodstuffModal] = useState(false);

  const fetchFoodstuffs = useCallback(
    async (name, page, withBarcodeParam, withBrandParam, unverifiedOnlyParam) => {
      if (page < 0) {
        fetchFoodstuffs(name, 0);
        return;
      }

      setIsLoading(true);
      setCurrentFoodstuffsPage(page + 1);
      const onlyUnverified = unverifiedOnlyParam !== undefined ? unverifiedOnlyParam : unverifiedOnly;
      try {
        const foodstuffsResponse = await foodstuffService.getFoodstuffs(
          page,
          7,
          name,
          withBarcodeParam !== undefined ? withBarcodeParam : withBarcode,
          withBrandParam !== undefined ? withBrandParam : withBrand,
          onlyUnverified ? "UNVERIFIED" : undefined,
        );
        setFoodstuffsResponse(foodstuffsResponse);
        setFoodstuffs(foodstuffsResponse.content);

        if (page > foodstuffsResponse.total_pages) {
          fetchFoodstuffs(name, foodstuffsResponse.total_pages - 1);
        }
      } catch (error) {
        console.log("Erro ao buscar alimentos", error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFoodstuffsPage, withBarcode, withBrand, unverifiedOnly]
  );

  const handleDeleteFoodstuff = async () => {
    try {
      await foodstuffService.deleteFoodstuff(foodstuffOnDelete.id);
      setOpenDeleteConfirmModal(false);
      setFoodstuffOnDelete();
      await fetchFoodstuffs("", 0);
    } catch (error) {
      setDeleteFoodstuffError(error);
      console.log("Erro ao deletar alimento", error);
    }
  };

  const deleteDescription = (
    <>
      Ao excluir o alimento <b>{foodstuffOnDelete?.name}</b>, todas as
      informações relacionadas a ele serão perdidas.
    </>
  );

  const getUnitOfMeasurement = (unit) => {
    switch (unit) {
      case "GRAMS":
        return "g";
      case "MILLILITERS":
        return "ml";
      case "LITERS":
        return "l";
      case "KILOGRAMS":
        return "kg";
      case "UNITS":
        return "un";
      default:
        return "g";
    }
  };

  const formatGroupLabel = (group) => FOOD_GROUP_LABELS[group] || group;

  const formatFoodstuffMeta = (foodstuff) => {
    const parts = [];

    if (foodstuff.baseline_weight != null) {
      parts.push(
        `${foodstuff.baseline_weight} ${getUnitOfMeasurement(foodstuff.unit_of_measurement)}`
      );
    }

    parts.push(foodstuff?.brand?.name || "Sem marca");
    parts.push(formatGroupLabel(foodstuff.group));

    return parts.join(" · ");
  };

  const getParams = () => {
    return `?currentPage=${currentFoodstuffsPage}${withBarcode !== undefined ? `&withBarcode=${withBarcode}` : ""}${withBrand === true || withBrand === false ? `&withBrand=${withBrand}` : ""}${unverifiedOnly ? "&unverified=true" : ""}`;
  };

  useEffect(() => {
    document.title = "Alimentos";
    setSelectedScreen("foodstuffs");
    const pageFromParam = foodstuffPageParam ? Number(foodstuffPageParam) : 0;
    const withBarcodeFromParam = withBarcodeParam ? withBarcodeParam === "true" : undefined;
    const withBrandFromParam =
      withBrandParam === "true" ? true : withBrandParam === "false" ? false : undefined;
    const unverifiedFromParam = unverifiedParam === "true";

    setCurrentFoodstuffsPage(pageFromParam ? Number(pageFromParam) : 1);
    setWithBarcode(withBarcodeFromParam);
    setWithBrand(withBrandFromParam);
    setUnverifiedOnly(unverifiedFromParam);

    async function fetchData() {
      await Promise.all([
        fetchFoodstuffs("", pageFromParam, withBarcodeFromParam, withBrandFromParam, unverifiedFromParam),
      ]);
    }

    fetchData();
  }, []);

  useEffect(() => {
    const pageFromParam = foodstuffPageParam ? Number(foodstuffPageParam) : 1;
    if (pageFromParam) {
      setCurrentFoodstuffsPage(Number(pageFromParam));
    }
  }, [foodstuffPageParam]);

  return (
    <>
      <div className="right-side">
        <div className="right-side__content">
          <div className="right-side__content__header">
            <h1>
              Alimentos
              {foodstuffsResponse?.total_elements ? ` (${foodstuffsResponse.total_elements})` : ""}
            </h1>
          </div>
          <div className="foodstuffs">
            <div className="foodstuffs__content">
              <div className="foodstuffs__search">
                <div className="foodstuffs__search__name">
                  <TextInput
                    label="Nome, marca..."
                    placeholder="Nome, marca..."
                    onChangeText={(e) => {
                      setSearchedFoodstuffName(e);
                      fetchFoodstuffs(e, 0);
                    }}
                  />
                </div>
                <Dropdown
                  show={openOptionsDropdown}
                  onToggle={() => setOpenOptionsDropdown((prevState) => !prevState)}
                >
                  <a
                    className={`foodstuff__search__filter ${openOptionsDropdown && "foodstuff__search__filter--pressed"}`}
                    href="javascript:;"
                    onClick={() => setOpenOptionsDropdown((prevState) => !prevState)}
                  >
                    <img src={FilterIcon} alt="Filter icon" />
                  </a>
                  <Dropdown.Menu bsPrefix="dropdown-menu foodstuff__search__filter__dropdown">
                    <Dropdown.Header>
                      Filtro Avançado
                    </Dropdown.Header>
                    <div className="foodstuff__search__filter__dropdown__options">
                      <div className="foodstuff__search__filter__dropdown__option">
                        <a className="foodstuff__search__filter__dropdown__option__checkbox" href="javascript:;" onClick={() => {
                          const withBarcodeParam = withBarcode !== undefined ? withBarcode : false;
                          setWithBarcode(() => {
                            fetchFoodstuffs(searchedFoodstuffName, currentFoodstuffsPage - 1, !withBarcodeParam, withBrand);
                            return !withBarcodeParam;
                          })
                        }}>
                          <div className={`foodstuff__search__filter__dropdown__option__checkbox__box ${withBarcode && 'checkbox--checked'}`} >
                            <img src={CheckedIcon} alt="Checked icon" />
                          </div>
                          <div className="foodstuff__search__filter__dropdown__option__checkbox__label">Possui código de barras</div>
                        </a>
                      </div>
                      <div className="foodstuff__search__filter__dropdown__option">
                        <a className="foodstuff__search__filter__dropdown__option__checkbox" href="javascript:;" onClick={() => {
                          const nextWithBrand = nextTriStateFilter(withBrand);
                          setWithBrand(nextWithBrand);
                          fetchFoodstuffs(
                            searchedFoodstuffName,
                            currentFoodstuffsPage - 1,
                            withBarcode,
                            nextWithBrand,
                          );
                        }}>
                          <div
                            className={`foodstuff__search__filter__dropdown__option__checkbox__box ${
                              withBrand === true
                                ? "checkbox--checked"
                                : withBrand === false
                                  ? "checkbox--excluded"
                                  : ""
                            }`}
                          >
                            {withBrand === true && (
                              <img src={CheckedIcon} alt="Com marca" />
                            )}
                            {withBrand === false && (
                              <img src={UncheckedXIcon} alt="Sem marca" />
                            )}
                          </div>
                          <div className="foodstuff__search__filter__dropdown__option__checkbox__label">Possui marca</div>
                        </a>
                      </div>
                      <div className="foodstuff__search__filter__dropdown__option">
                        <a className="foodstuff__search__filter__dropdown__option__checkbox" href="javascript:;" onClick={() => {
                          const nextUnverifiedOnly = !unverifiedOnly;
                          setUnverifiedOnly(nextUnverifiedOnly);
                          fetchFoodstuffs(
                            searchedFoodstuffName,
                            currentFoodstuffsPage - 1,
                            withBarcode,
                            withBrand,
                            nextUnverifiedOnly,
                          );
                        }}>
                          <div className={`foodstuff__search__filter__dropdown__option__checkbox__box ${unverifiedOnly && 'checkbox--checked'}`} >
                            <img src={CheckedIcon} alt="Checked icon" />
                          </div>
                          <div className="foodstuff__search__filter__dropdown__option__checkbox__label">Não verificado</div>
                        </a>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
                <div className="foodstuffs__button">
                  <DefaultButton
                    title="Novo alimento"
                    onClick={() => setShowNewFoodstuffModal(true)}
                  />
                </div>
              </div>
              <div className="foodstuffs__list">
                <div className="foodstuffs__list__header">
                  <div className="foodstuffs__list__header__image">
                    Imagem
                  </div>
                  <div className="foodstuffs__list__header__name">Nome</div>
                  <div className="foodstuffs__list__header__type">Tipo</div>
                  <div className="foodstuffs__list__header__measurement">
                    Medida
                  </div>
                  <div className="foodstuffs__list__header__brand">Marca</div>
                  <div className="foodstuffs__list__header__weight">Peso base</div>
                  <div className="foodstuffs__list__header__actions" />
                </div>
                <div className="foodstuffs__list__items">
                  {foodstuffs.map((foodstuff) => {
                    const foodstuffName = foodstuff.name || "";
                    const isLongName = foodstuffName.length > NAME_COLLAPSE_MAX_LENGTH;
                    const isExpanded = expandedNameRowId === foodstuff.id;

                    return (
                    <div
                      className={`foodstuffs__list__item ${isExpanded ? "foodstuffs__list__item--expanded" : ""}`}
                      key={foodstuff.id}
                      onClick={() => {
                        window.location.href = `/foodstuffs/${foodstuff.id}/editing${getParams()}`;
                      }}
                    >
                      <div className="foodstuffs__list__item__image">
                        <img src={foodstuff.photo_url ?? DefaultFoodstuffIcon} alt="" />
                      </div>
                      <div className="foodstuffs__list__item__info">
                        <div
                          className={`foodstuffs__list__item__name ${isLongName && !isExpanded ? "foodstuffs__list__item__name--collapsed" : ""}`}
                        >
                          <span className="foodstuffs__list__item__name-text">
                            {isLongName && !isExpanded
                              ? truncateName(foodstuffName, NAME_COLLAPSE_MAX_LENGTH)
                              : foodstuffName}
                          </span>
                          {foodstuff.status === "UNVERIFIED" && (
                            <span className="foodstuffs__list__item__unverified-badge">Não verificado</span>
                          )}
                          {isLongName && (
                            <button
                              type="button"
                              className="foodstuffs__list__item__name-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedNameRowId(isExpanded ? null : foodstuff.id);
                              }}
                            >
                              {isExpanded ? "Ver menos" : "Ver mais"}
                            </button>
                          )}
                        </div>
                        <div className="foodstuffs__list__item__meta">
                          {formatFoodstuffMeta(foodstuff)}
                        </div>
                      </div>
                      <div className="foodstuffs__list__item__type foodstuffs__list__item__field--desktop">
                        {formatGroupLabel(foodstuff.group)}
                      </div>
                      <div className="foodstuffs__list__item__measurement foodstuffs__list__item__field--desktop">
                        {foodstuff.unit_of_measurement}
                      </div>
                      <div
                        className={`foodstuffs__list__item__brand foodstuffs__list__item__field--desktop ${foodstuff?.brand?.name ? '' : 'foodstuffs__list__item__brand--not-appliable'}`}
                      >
                        {foodstuff?.brand?.name ?? "Não aplicável"}
                      </div>
                      <div className="foodstuffs__list__item__weight foodstuffs__list__item__field--desktop">
                        {`${foodstuff?.baseline_weight} ${getUnitOfMeasurement(foodstuff.unit_of_measurement)}`}
                      </div>
                      <div
                        className="foodstuffs__list__item__actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Options items={
                          [
                            {
                              title: "Editar",
                              onClick: () => (window.location.href = `/foodstuffs/${foodstuff.id}/editing${getParams()}`),
                            },
                            {
                              title: "Duplicar",
                              onClick: () => { window.location.href = `/create-foodstuff?draftFoodstuffId=${foodstuff.id}` },
                            },
                            {
                              title: "Deletar",
                              onClick: () => {
                                setOpenDeleteConfirmModal(true);
                                setFoodstuffOnDelete(foodstuff);
                              },
                              className: "delete-option",
                            },
                          ]
                        } />
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="foodstuffs__list__footer">
                  {currentFoodstuffsPage && currentFoodstuffsPage <= foodstuffsResponse.total_pages && (
                    <NumericPagination
                      currentPage={currentFoodstuffsPage === 0 ? 1 : Number(currentFoodstuffsPage)}
                      totalPages={foodstuffsResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => {
                        fetchFoodstuffs(searchedFoodstuffName, page);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        title="Confirmação de exclusão"
        description={deleteDescription}
        onConfirm={handleDeleteFoodstuff}
        onCancel={() => {
          setOpenDeleteConfirmModal(false);
          setFoodstuffOnDelete();
        }}
        show={openDeleteConfirmModal}
        setShow={setOpenDeleteConfirmModal}
      />
      <NewFoodstuffModal
        show={showNewFoodstuffModal}
        onClose={() => setShowNewFoodstuffModal(false)}
      />
    </>
  );
};

export default Foodstuffs;
